import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { loginUser } from '../reducers/UserProfile/actions';
import EmbeddedLoginModal from '../app/main/helperComponents/EmbeddedLoginModal';
import { AUTH_GATE_EVENTS } from './authGate';
import { isAuthenticated } from './auth';
import analytics from './analytics';

const AuthGateProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const hasHistoryEntryRef = useRef(false); // Track if we've pushed a history entry

  const onClose = useCallback(() => {
    setIsModalOpen(false);
    setPendingAction(null);
    setReferralCode(null);
    
    // When user clicks outside the modal, just replace the state
    // Don't call window.history.back() as that causes navigation
    // The popstate handler will handle the actual back button press
    try {
      if (typeof window !== 'undefined' && window.history && window.history.state) {
        const currentState = window.history.state;
        if (currentState && currentState.authModal) {
          // Replace the authModal state with a clean state
          window.history.replaceState({}, '');
        }
      }
    } catch (_) {}
  }, []);

  const handleLoginSuccess = useCallback(() => {
    if (pendingAction && typeof pendingAction === 'function') {
      try {
        pendingAction();
      } catch (error) {
        console.error('Error executing pending action:', error);
      }
    }
    onClose();
  }, [pendingAction, onClose]);

  useEffect(() => {
    const handleLoginRequired = (event) => {
      const { pendingAction: action, referral_code } = event.detail || {};
      // Emit only on transition closed → open
      if (!isModalOpen) {
        try {
          analytics.trackPlatformEvent('view_login_modal', {
            source: (event.detail && event.detail.source) || 'unknown',
            entry_point: 'embedded_modal',
            referral_code: referral_code || null,
            is_guest: !isAuthenticated(),
            prev_path: (typeof window !== 'undefined' && window.location && window.location.pathname) ? window.location.pathname : ''
          });
        } catch (_) {}
      }
      setPendingAction(action);
      setReferralCode(referral_code);
      setIsModalOpen(true);
      
      // Push a history state so Android back (WebView) will close the modal instead of leaving it stuck
      // Only push ONCE per page session to prevent multiple history entries
      if (!hasHistoryEntryRef.current) {
        try {
          if (typeof window !== 'undefined' && window.history && window.history.pushState) {
            window.history.pushState({ authModal: true }, '');
            hasHistoryEntryRef.current = true;
          }
        } catch (_) {}
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(AUTH_GATE_EVENTS.LOGIN_REQUIRED_EVENT, handleLoginRequired);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(AUTH_GATE_EVENTS.LOGIN_REQUIRED_EVENT, handleLoginRequired);
      }
    };
  }, [isModalOpen]);

  // Close modal on browser back (popstate) if it is open
  useEffect(() => {
    if (!isModalOpen) return;
    const onPopState = (e) => {
      try { 
        setIsModalOpen(false);
        setPendingAction(null);
        setReferralCode(null);
        // Reset the flag so next page visit can push history again
        hasHistoryEntryRef.current = false;
        // When back button is pressed, don't go back further
        // The modal was already closed by the popstate event
      } catch (_) {}
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', onPopState);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('popstate', onPopState);
      }
    };
  }, [isModalOpen]);

  // Global mobile auth listeners (works even if login components are not mounted)
  useEffect(() => {
    const onMobileSuccess = (event) => {
      try {
        const detail = event?.detail || {};
        const token = detail.userToken || detail.idToken || detail.token;
        const ref = detail.referral_code || referralCode;
        if (token) {
          dispatch(loginUser(token, ref));
          // Close modal immediately to avoid it lingering on top in WebView
          try { setIsModalOpen(false); } catch (_) {}
          // In React Native WebView, force a navigation to home shortly after
          try {
            if (window.ReactNativeWebView) {
              setTimeout(() => {
                try { window.location.replace('/'); } catch (_) {}
              }, 800);
            }
          } catch (_) {}
        }
      } catch (_) {}
    };
    const onMobileFailure = (event) => {
      // no-op: UI-specific error toasts handled in modal/page where applicable
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('mobileAuthSuccess', onMobileSuccess);
      window.addEventListener('mobileAuthFailure', onMobileFailure);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('mobileAuthSuccess', onMobileSuccess);
        window.removeEventListener('mobileAuthFailure', onMobileFailure);
      }
    };
  }, [dispatch, referralCode]);

  // track auth state to hide/show CTA immediately when it changes
  const [, setAuthTick] = useState(0);
  useEffect(() => {
    const onAuthChanged = () => setAuthTick((t) => t + 1);
    if (typeof window !== 'undefined') {
      window.addEventListener('AUTH_STATE_CHANGED', onAuthChanged);
      window.addEventListener('storage', onAuthChanged);
      // If already authenticated when provider mounts (e.g., mobile returns), ensure modal is closed
      try {
        if (isAuthenticated()) {
          setIsModalOpen(false);
        }
      } catch (_) {}
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('AUTH_STATE_CHANGED', onAuthChanged);
        window.removeEventListener('storage', onAuthChanged);
      }
    };
  }, []);

  // If auth becomes valid while modal is open (e.g., after Google popup),
  // automatically complete the gate to avoid any stuck overlays
  useEffect(() => {
    if (isModalOpen && isAuthenticated()) {
      handleLoginSuccess();
    }
  }, [isModalOpen, handleLoginSuccess]);

  return (
    <>
      {children}
      <EmbeddedLoginModal
        isOpen={isModalOpen}
        onClose={onClose}
        referral_code={referralCode}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
};

export default AuthGateProvider;


